import React from "react";
import DOMPurify from "dompurify";

function styleObj(style = {}) {
  return {
    margin: style.margin || undefined,
    padding: style.padding || undefined,
    color: style.color || undefined,
    backgroundColor: style.backgroundColor || undefined,
    fontSize: style.fontSize || undefined,
    textAlign: style.textAlign || undefined,
    fontFamily: style.fontFamily || undefined,
    fontWeight: style.fontWeight || undefined,
    fontStyle: style.fontStyle || undefined,
    borderRadius: style.borderRadius || undefined,
    border: style.border || undefined
  };
}

function inlineToHtml(input) {
  const escaped = String(input ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
  const withLinks = escaped.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    (_m, label, url) =>
      `<a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a>`
  );
  return DOMPurify.sanitize(withLinks.replace(/\n/g, "<br/>"));
}

export function CmsPageBuilderRenderer({ blocks }) {
  const renderList = (items = [], style = {}, key = "list") => {
    const clean = (Array.isArray(items) ? items : []).map((x) => String(x ?? "").trim()).filter(Boolean);
    if (!clean.length) return null;
    return (
      <ul key={key} style={style} className="list-disc pl-5">
        {clean.map((item, i) => (
          <li key={i} dangerouslySetInnerHTML={{ __html: inlineToHtml(item) }} />
        ))}
      </ul>
    );
  };

  const renderNestedBlock = (nested, idx) => {
    if (!nested) return null;
    if (nested.type === "heading")
      return <h4 key={idx} className="mb-2 text-base font-semibold" dangerouslySetInnerHTML={{ __html: inlineToHtml(nested.content?.text || "Heading") }} />;
    if (nested.type === "text")
      return <p key={idx} className="mb-2" dangerouslySetInnerHTML={{ __html: inlineToHtml(nested.content?.text || "") }} />;
    if (nested.type === "image")
      return (
        <figure key={idx} className="mb-2">
          <img src={nested.content?.src || ""} alt={nested.content?.alt || ""} className="max-w-full rounded-lg" />
        </figure>
      );
    if (nested.type === "list") return renderList(nested.content?.items || [], {}, `nl-${idx}`);
    return null;
  };

  return (
    <div className="space-y-3">
      {blocks.map((block) => {
        const style = styleObj(block.style);
        if (block.type === "heading")
          return <h2 key={block.id} style={style} dangerouslySetInnerHTML={{ __html: inlineToHtml(block.content?.text || "Heading") }} />;
        if (block.type === "text")
          return <p key={block.id} style={style} dangerouslySetInnerHTML={{ __html: inlineToHtml(block.content?.text || "") }} />;
        if (block.type === "description") {
          const parts = String(block.content?.text || "")
            .split(/\n{2,}/)
            .map((x) => x.trim())
            .filter(Boolean);
          return (
            <section key={block.id} style={style} className="space-y-3">
              {parts.map((part, idx) => (
                <p key={idx} dangerouslySetInnerHTML={{ __html: inlineToHtml(part) }} />
              ))}
            </section>
          );
        }
        if (block.type === "image")
          return (
            <figure key={block.id} style={style}>
              <img src={block.content?.src || ""} alt={block.content?.alt || ""} className="max-w-full rounded-lg" />
            </figure>
          );
        if (block.type === "button")
          return (
            <p key={block.id} style={style}>
              <a href={block.content?.href || "#"} className="inline-block rounded-lg bg-slate-900 px-3 py-2 text-white no-underline">
                {block.content?.label || "Button"}
              </a>
            </p>
          );
        if (block.type === "list") {
          return renderList(block.content?.items || [], style, block.id);
        }
        if (block.type === "section")
          return (
            <section key={block.id} style={style} className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="rounded-lg border border-slate-200 p-3 whitespace-pre-wrap">{block.content?.leftText || ""}</div>
              <div className="rounded-lg border border-slate-200 p-3 whitespace-pre-wrap">{block.content?.rightText || ""}</div>
            </section>
          );
        if (block.type === "columns") {
          const cols = Math.max(1, Math.min(4, Number(block.content?.columns || 3)));
          const items = Array.isArray(block.content?.items) ? block.content.items : [];
          return (
            <section key={block.id} style={style} className={`cms-columns cms-columns-${cols}`}>
              {new Array(cols).fill(null).map((_, i) => (
                <div key={i} className="cms-col whitespace-pre-wrap">
                  {Array.isArray(items[i]?.blocks) && items[i].blocks.length > 0
                    ? items[i].blocks.map((nested, idx) => renderNestedBlock(nested, idx))
                    : <span dangerouslySetInnerHTML={{ __html: inlineToHtml(`Column ${i + 1} text`) }} />}
                </div>
              ))}
            </section>
          );
        }
        if (block.type === "box")
          return (
            <section key={block.id} style={style} className="rounded-lg border border-slate-200 p-3">
              <h3 className="mb-2 text-lg font-semibold" dangerouslySetInnerHTML={{ __html: inlineToHtml(block.content?.title || "Box Title") }} />
              <p className="whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: inlineToHtml(block.content?.text || "") }} />
            </section>
          );
        if (block.type === "table") {
          const headers = Array.isArray(block.content?.headers) ? block.content.headers : [];
          const rows = Array.isArray(block.content?.rows) ? block.content.rows : [];
          const caption = String(block.content?.caption || "").trim();
          return (
            <div key={block.id} style={style} className="overflow-x-auto">
              {caption ? <p className="mb-2 text-sm font-semibold">{caption}</p> : null}
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    {headers.map((header, i) => (
                      <th key={i} className="border border-slate-200 bg-slate-50 px-2 py-1.5 text-left">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, r) => (
                    <tr key={r}>
                      {headers.map((_, c) => (
                        <td key={c} className="border border-slate-200 px-2 py-1.5">
                          {Array.isArray(row) ? row[c] || "" : ""}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

